import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface File {
  id: string;
  name: string;
  path: string;
  size: number;
  git_status?: string;
  created_at: number;
  updated_at: number;
}

interface Project {
  id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  github_repo?: string;
  github_branch?: string;
}

export default function ProjectFiles() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [creating, setCreating] = useState(false);
  const [githubConnecting, setGithubConnecting] = useState(false);
  const [githubRepo, setGithubRepo] = useState('');
  const [githubBranch, setGithubBranch] = useState('main');
  const [githubToken, setGithubToken] = useState('');
  const [pulling, setPulling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchFiles();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`/api/projects/${projectId}`);
      setProject(response.data);
      if (response.data.github_repo) {
        setGithubRepo(response.data.github_repo);
        setGithubBranch(response.data.github_branch || 'main');
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project');
    }
  };

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/projects/${projectId}/files`);
      setFiles(response.data.files || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const createFile = async () => {
    if (!newFileName.trim()) return;
    
    try {
      setCreating(true);
      const response = await axios.post(`/api/projects/${projectId}/files`, {
        name: newFileName,
        content: `# ${newFileName}\n\nNew file created on ${new Date().toLocaleDateString()}`
      });
      
      setFiles([...files, response.data]);
      setNewFileName('');
      setError(null);
      
      // Navigate to the new file
      navigate(`/projects/${projectId}/files/${response.data.id}`);
    } catch (err) {
      console.error('Error creating file:', err);
      setError('Failed to create file');
    } finally {
      setCreating(false);
    }
  };

  const connectGitHub = async () => {
    if (!githubRepo.trim() || !githubToken.trim()) return;
    
    try {
      setGithubConnecting(true);
      await axios.post(`/api/projects/${projectId}/github/connect`, {
        github_repo: githubRepo,
        github_branch: githubBranch,
        github_access_token: githubToken
      });
      
      // Refresh project data
      await fetchProject();
      setError(null);
    } catch (err) {
      console.error('Error connecting GitHub:', err);
      setError('Failed to connect GitHub repository');
    } finally {
      setGithubConnecting(false);
    }
  };

  const pullFromGitHub = async () => {
    try {
      setPulling(true);
      await axios.post(`/api/projects/${projectId}/github/pull`, {});
      
      // Refresh files
      await fetchFiles();
      setError(null);
    } catch (err) {
      console.error('Error pulling from GitHub:', err);
      setError('Failed to pull from GitHub');
    } finally {
      setPulling(false);
    }
  };

  const pushToGitHub = async () => {
    if (!commitMessage.trim() || selectedFiles.length === 0) return;
    
    try {
      setPushing(true);
      await axios.post(`/api/projects/${projectId}/github/push`, {
        commit_message: commitMessage,
        files: selectedFiles
      });
      
      // Refresh files
      await fetchFiles();
      setCommitMessage('');
      setSelectedFiles([]);
      setError(null);
    } catch (err) {
      console.error('Error pushing to GitHub:', err);
      setError('Failed to push to GitHub');
    } finally {
      setPushing(false);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    if (selectedFiles.includes(fileId)) {
      setSelectedFiles(selectedFiles.filter(id => id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, fileId]);
    }
  };

  if (loading && files.length === 0) {
    return <div className="p-4">Loading files...</div>;
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        {project?.organization_id && (
          <Link to={`/organizations/${project.organization_id}`} className="text-blue-500 hover:underline">
            ← Back to {project.organization_name || 'Organization'} Projects
          </Link>
        )}
      </div>
      
      <h1 className="text-2xl font-bold mb-1">
        {project ? project.name : 'Project'} Files
      </h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h2 className="text-lg font-semibold mb-2">Create New File</h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder="New file name (e.g., document.md)"
              className="flex-1 px-3 py-2 border rounded"
            />
            <button
              onClick={createFile}
              disabled={creating || !newFileName.trim()}
              className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-blue-300"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
        
        {project?.github_repo ? (
          <div>
            <h2 className="text-lg font-semibold mb-2">GitHub Integration</h2>
            <div className="text-sm mb-2">
              Connected to: {project.github_repo}:{project.github_branch || 'main'}
            </div>
            <div className="flex gap-2">
              <button
                onClick={pullFromGitHub}
                disabled={pulling}
                className="bg-green-500 text-white px-4 py-2 rounded disabled:bg-green-300"
              >
                {pulling ? 'Pulling...' : 'Pull'}
              </button>
              <button
                onClick={() => document.getElementById('push-modal')?.classList.remove('hidden')}
                className="bg-purple-500 text-white px-4 py-2 rounded"
              >
                Push
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="text-lg font-semibold mb-2">Connect GitHub Repository</h2>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={githubRepo}
                onChange={(e) => setGithubRepo(e.target.value)}
                placeholder="GitHub repo (e.g., username/repo)"
                className="px-3 py-2 border rounded"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={githubBranch}
                  onChange={(e) => setGithubBranch(e.target.value)}
                  placeholder="Branch (default: main)"
                  className="flex-1 px-3 py-2 border rounded"
                />
                <input
                  type="password"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  placeholder="GitHub token"
                  className="flex-1 px-3 py-2 border rounded"
                />
              </div>
              <button
                onClick={connectGitHub}
                disabled={githubConnecting || !githubRepo.trim() || !githubToken.trim()}
                className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-blue-300"
              >
                {githubConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {files.length === 0 ? (
        <div className="text-gray-500">No files found. Create one to get started.</div>
      ) : (
        <div className="border rounded overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {project?.github_repo && (
                  <th className="w-12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFiles(files.map(file => file.id));
                        } else {
                          setSelectedFiles([]);
                        }
                      }}
                      checked={selectedFiles.length === files.length && files.length > 0}
                      className="h-4 w-4"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                {project?.github_repo && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map(file => (
                <tr key={file.id} className="hover:bg-gray-50">
                  {project?.github_repo && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onChange={() => toggleFileSelection(file.id)}
                        className="h-4 w-4"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link
                      to={`/projects/${projectId}/files/${file.id}`}
                      className="text-blue-500 hover:underline"
                    >
                      {file.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {file.size} bytes
                  </td>
                  {project?.github_repo && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {file.git_status === 'synced' ? (
                        <span className="text-green-500">Synced</span>
                      ) : file.git_status === 'modified' ? (
                        <span className="text-yellow-500">Modified</span>
                      ) : file.git_status === 'new' ? (
                        <span className="text-blue-500">New</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {new Date(file.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Push Modal */}
      <div id="push-modal" className="hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">Push to GitHub</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commit Message
            </label>
            <textarea
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              rows={3}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selected Files ({selectedFiles.length})
            </label>
            <div className="max-h-40 overflow-y-auto border rounded p-2">
              {selectedFiles.length === 0 ? (
                <div className="text-gray-500">No files selected</div>
              ) : (
                <ul className="text-sm">
                  {selectedFiles.map(fileId => {
                    const file = files.find(f => f.id === fileId);
                    return (
                      <li key={fileId} className="py-1">
                        {file?.name || fileId}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => document.getElementById('push-modal')?.classList.add('hidden')}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                pushToGitHub();
                document.getElementById('push-modal')?.classList.add('hidden');
              }}
              disabled={pushing || !commitMessage.trim() || selectedFiles.length === 0}
              className="bg-purple-500 text-white px-4 py-2 rounded disabled:bg-purple-300"
            >
              {pushing ? 'Pushing...' : 'Push'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
